import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PLAYER_COLORS } from '../../../utils/constants.js';
import { PALETTE, colored, vary, seededRandom, jitter } from '../../Style.js';

// Undyed cloth for unclaimed guardhouses
const NEUTRAL_CLOTH = PALETTE.cloth;
const NEUTRAL_RING = 0x9a9486;
const PLASTER = 0xe3d6b4;
const CHARRED = 0x4a4038;

const WALL_BASE = 0.6; // Top of the stone footing
const WALL_TOP = 2.6;
const HALF = 2; // Half-width of the hall

// A guardhouse: timber-framed hall on a stone footing under a thatched gable
// roof, with a lantern by the door, the owner's banners either side of it and
// a flag on a pole at the corner.
export class Barracks {
  constructor(scene, barracks) {
    this.scene = scene;
    this.barracks = barracks;
    this.meshGroup = new THREE.Group();
    this.buildingMesh = null;
    this.roofMesh = null;
    this.flagMesh = null;
    this.rangeMesh = null;
    this.healthBarGroup = null;
    this.healingIndicator = null;
    this.rand = seededRandom(hashId(barracks.id));
    this.wasDestroyed = false;
    this.create();
  }

  create() {
    const color = this.getColor();
    const neutral = this.barracks.ownerId === -1;

    // Per-guardhouse materials so a destroyed one can char and fade alone
    this.structureMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9, flatShading: true, vertexColors: true });
    this.teamMaterial = new THREE.MeshStandardMaterial({
      color: this.getClothColor(),
      roughness: 0.9,
      flatShading: true,
      side: THREE.DoubleSide,
      emissive: color,
      emissiveIntensity: neutral ? 0 : 0.15
    });
    this.lanternMaterial = new THREE.MeshStandardMaterial({
      color: PALETTE.lanternGlow,
      roughness: 0.8,
      flatShading: true,
      emissive: PALETTE.lanternGlow,
      emissiveIntensity: 1.2
    });

    // Flagstone apron and stone footing
    const footing = new THREE.Mesh(mergeGeometries(this.buildFooting()), this.structureMaterial);
    footing.castShadow = true;
    footing.receiveShadow = true;
    this.meshGroup.add(footing);

    // Timber-framed walls, door and flag pole
    this.buildingMesh = new THREE.Mesh(mergeGeometries(this.buildWalls()), this.structureMaterial);
    this.buildingMesh.castShadow = true;
    this.buildingMesh.receiveShadow = true;
    this.meshGroup.add(this.buildingMesh);

    // Thatched gable roof
    this.roofMesh = new THREE.Mesh(mergeGeometries(this.buildRoof()), this.structureMaterial);
    this.roofMesh.castShadow = true;
    this.roofMesh.receiveShadow = true;
    this.meshGroup.add(this.roofMesh);

    // Lantern beside the door
    this.lanternMesh = new THREE.Mesh(new THREE.OctahedronGeometry(0.16, 0), this.lanternMaterial);
    this.lanternMesh.scale.set(1, 1.4, 1);
    this.lanternMesh.position.set(0.75, 1.95, HALF + 0.38);
    this.meshGroup.add(this.lanternMesh);

    // Banners hanging either side of the door
    const banners = [];
    for (const x of [-1.3, 1.3]) {
      const banner = new THREE.ShapeGeometry(bannerShape(0.75, 1.35));
      banner.translate(x, WALL_TOP - 0.15, HALF + 0.08);
      banners.push(banner);
    }
    this.bannerMesh = new THREE.Mesh(mergeGeometries(banners), this.teamMaterial);
    this.bannerMesh.castShadow = true;
    this.meshGroup.add(this.bannerMesh);

    // Flag (shows ownership), rippling in the wind
    const flagGeometry = new THREE.PlaneGeometry(1.5, 1, 6, 2);
    flagGeometry.translate(0.75, 0, 0);
    this.flagMesh = new THREE.Mesh(flagGeometry, this.teamMaterial);
    this.flagMesh.position.set(2.5, 3.5, 2.5);
    this.flagMesh.castShadow = true;
    this.flagMesh.userData.base = Float32Array.from(flagGeometry.attributes.position.array);
    this.meshGroup.add(this.flagMesh);

    // Range indicator (shows claim radius)
    const rangeGeometry = new THREE.RingGeometry(7.5, 8, 32);
    const rangeMaterial = new THREE.MeshBasicMaterial({
      color: this.getRingColor(),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.25
    });
    this.rangeMesh = new THREE.Mesh(rangeGeometry, rangeMaterial);
    this.rangeMesh.rotation.x = -Math.PI / 2;
    this.rangeMesh.position.y = 0.05;
    this.meshGroup.add(this.rangeMesh);

    // Health bar background
    this.healthBarGroup = new THREE.Group();
    this.healthBarGroup.position.y = 5.5;

    const healthBarBg = new THREE.Mesh(
      new THREE.BoxGeometry(4, 0.3, 0.1),
      new THREE.MeshBasicMaterial({ color: 0x333333 })
    );
    this.healthBarGroup.add(healthBarBg);

    // Health bar fill
    this.healthBarFill = new THREE.Mesh(
      new THREE.BoxGeometry(4, 0.3, 0.12),
      new THREE.MeshBasicMaterial({ color: 0x00ff00 })
    );
    this.healthBarFill.position.z = 0.01;
    this.healthBarGroup.add(this.healthBarFill);

    this.meshGroup.add(this.healthBarGroup);

    // Healing indicator (glowing cross icon) - hidden by default
    this.healingIndicator = new THREE.Group();
    this.healingIndicator.position.y = 6;
    this.healingIndicator.visible = false;

    // Cross shape using two boxes
    const crossMaterial = new THREE.MeshBasicMaterial({
      color: 0x7ee06a,
      transparent: true,
      opacity: 0.8
    });
    const crossVertical = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 1.2, 0.1),
      crossMaterial
    );
    const crossHorizontal = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, 0.3, 0.1),
      crossMaterial
    );
    this.healingIndicator.add(crossVertical);
    this.healingIndicator.add(crossHorizontal);

    // Outer glow ring
    const glowGeometry = new THREE.RingGeometry(0.8, 1, 16);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x7ee06a,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide
    });
    this.healingGlow = new THREE.Mesh(glowGeometry, glowMaterial);
    this.healingIndicator.add(this.healingGlow);

    this.meshGroup.add(this.healingIndicator);

    // Position the group
    this.meshGroup.position.set(
      this.barracks.position.x,
      this.barracks.position.y,
      this.barracks.position.z
    );

    this.scene.add(this.meshGroup);
  }

  buildFooting() {
    const rand = this.rand;
    const parts = [];

    // Packed earth under a loose grid of flagstones
    parts.push(colored(new THREE.BoxGeometry(6, 0.16, 6), PALETTE.dirtDark, { y: 0.08 }));
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        const geo = jitter(new THREE.BoxGeometry(1.36, 0.14, 1.36), 0.08, rand);
        parts.push(colored(geo, vary(PALETTE.stoneLight, rand, 0.14, 0.02), {
          x: -2.25 + i * 1.5, y: 0.2, z: -2.25 + j * 1.5, ry: (rand() - 0.5) * 0.1
        }));
      }
    }

    // Coursed stone footing the hall sits on
    parts.push(colored(new THREE.BoxGeometry(4.3, 0.4, 4.3), PALETTE.stoneDark, { y: 0.4 }));
    for (let side = 0; side < 4; side++) {
      let cursor = -2.25;
      while (cursor < 2.24) {
        const length = Math.min(0.6 + rand() * 0.7, 2.25 - cursor);
        const color = rand() < 0.15 ? vary(PALETTE.mossStone, rand) : vary(PALETTE.stone, rand, 0.14, 0.02);
        parts.push(colored(new THREE.BoxGeometry(Math.max(0.05, length - 0.05), 0.36, 0.3), color, {
          x: cursor + length / 2, y: 0.42, z: 2.12
        }).rotateY(side * Math.PI / 2));
        cursor += length;
      }
    }

    // Step up to the door
    parts.push(colored(new THREE.BoxGeometry(1.5, 0.3, 0.6), vary(PALETTE.stone, rand), { y: 0.3, z: 2.55 }));

    return parts;
  }

  buildWalls() {
    const rand = this.rand;
    const parts = [];
    const height = WALL_TOP - WALL_BASE;
    const midY = WALL_BASE + height / 2;

    // Plaster infill
    parts.push(colored(new THREE.BoxGeometry(HALF * 2 - 0.1, height, HALF * 2 - 0.1), PLASTER, { y: midY }));

    // Corner posts, sill and wall plates
    for (const x of [-HALF, HALF]) {
      for (const z of [-HALF, HALF]) {
        parts.push(colored(new THREE.BoxGeometry(0.28, height + 0.1, 0.28), PALETTE.woodDark, { x, y: midY, z }));
      }
    }
    for (let side = 0; side < 4; side++) {
      for (const y of [WALL_BASE + 0.08, WALL_TOP - 0.06]) {
        parts.push(colored(new THREE.BoxGeometry(HALF * 2, 0.18, 0.2), vary(PALETTE.woodDark, rand, 0.05), {
          y, z: HALF
        }).rotateY(side * Math.PI / 2));
      }
    }

    // Mid posts and braces on each wall; the front keeps its middle for the door
    const brace = Math.atan2(1.2, height - 0.3);
    for (let side = 0; side < 4; side++) {
      const ry = side * Math.PI / 2;
      const front = side === 0;
      for (const sx of [-1, 1]) {
        const postX = front ? sx * 0.72 : sx * 0.75;
        parts.push(colored(new THREE.BoxGeometry(0.2, height, 0.16), PALETTE.woodDark, {
          x: postX, y: midY, z: HALF + 0.02
        }).rotateY(ry));
        // Brace from the foot of the corner post up to the mid post
        if (!front) {
          parts.push(colored(new THREE.BoxGeometry(0.16, Math.hypot(height - 0.3, 1.2), 0.14), PALETTE.wood, {
            x: sx * 1.38, y: midY, z: HALF + 0.03, rz: sx * brace
          }).rotateY(ry));
        }
      }
    }

    // Plank door with iron bands under a lintel
    parts.push(colored(new THREE.BoxGeometry(1.1, 1.7, 0.12), PALETTE.wood, { y: WALL_BASE + 0.85, z: HALF + 0.06 }));
    for (const y of [WALL_BASE + 0.4, WALL_BASE + 1.3]) {
      parts.push(colored(new THREE.BoxGeometry(1.12, 0.08, 0.14), PALETTE.iron, { y, z: HALF + 0.07 }));
    }
    parts.push(colored(new THREE.BoxGeometry(1.5, 0.22, 0.3), PALETTE.woodDark, { y: WALL_BASE + 1.82, z: HALF + 0.05 }));

    // Small shuttered windows on the sides
    for (const sx of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.12, 0.55, 0.6), PALETTE.woodDark, { x: sx * (HALF + 0.03), y: WALL_BASE + 1.2 }));
    }

    // Lantern bracket
    parts.push(colored(new THREE.BoxGeometry(0.06, 0.06, 0.4), PALETTE.iron, { x: 0.75, y: 2.22, z: HALF + 0.2 }));
    parts.push(colored(new THREE.BoxGeometry(0.03, 0.08, 0.03), PALETTE.iron, { x: 0.75, y: 2.17, z: HALF + 0.38 }));

    // Flag pole with a brass finial
    parts.push(colored(new THREE.CylinderGeometry(0.08, 0.11, 4.2, 6), PALETTE.woodDark, { x: 2.5, y: 2.1, z: 2.5 }));
    parts.push(colored(new THREE.SphereGeometry(0.16, 6, 4), 0xd9b25a, { x: 2.5, y: 4.25, z: 2.5 }));

    // Banner rods
    for (const x of [-1.3, 1.3]) {
      parts.push(colored(new THREE.CylinderGeometry(0.04, 0.04, 0.9, 4), PALETTE.woodDark, { rz: Math.PI / 2, x, y: WALL_TOP - 0.13, z: HALF + 0.1 }));
    }

    return parts;
  }

  buildRoof() {
    const rand = this.rand;
    const parts = [];
    const pitch = 0.72;
    const span = HALF + 0.45; // Half-span including the eaves overhang
    const slope = span / Math.cos(pitch);
    const rise = span * Math.tan(pitch);
    const length = HALF * 2 + 0.8;

    // Two lumpy thatch slopes, ridge running along X
    for (const sz of [-1, 1]) {
      const slab = jitter(new THREE.BoxGeometry(length, 0.36, slope, 4, 1, 3), 0.12, rand);
      parts.push(colored(slab, vary(PALETTE.thatch, rand, 0.05), {
        rx: sz * pitch, y: WALL_TOP + rise / 2 + 0.2, z: sz * span / 2
      }));
    }

    // Gable ends
    const gable = new THREE.Shape();
    gable.moveTo(-HALF - 0.05, 0);
    gable.lineTo(HALF + 0.05, 0);
    gable.lineTo(0, rise - 0.1);
    gable.closePath();
    for (const sx of [-1, 1]) {
      const geo = new THREE.ExtrudeGeometry(gable, { depth: 0.15, bevelEnabled: false });
      parts.push(colored(geo, PLASTER, { ry: Math.PI / 2, x: sx * (HALF - 0.05) - 0.075, y: WALL_TOP }));
      // Timber king post on the gable
      parts.push(colored(new THREE.BoxGeometry(0.16, rise - 0.4, 0.16), PALETTE.woodDark, {
        x: sx * (HALF + 0.06), y: WALL_TOP + (rise - 0.4) / 2
      }));
    }

    // Bound ridge
    parts.push(colored(new THREE.CylinderGeometry(0.24, 0.24, length + 0.1, 6), vary(PALETTE.hay, rand, 0.05), {
      rz: Math.PI / 2, y: WALL_TOP + rise + 0.27
    }));

    return parts;
  }

  getColor() {
    if (this.barracks.ownerId === -1) {
      return 0x888888; // Gray for neutral
    }
    // Parse hex color string to number
    const colorStr = PLAYER_COLORS[this.barracks.ownerId];
    return parseInt(colorStr.replace('#', ''), 16);
  }

  getClothColor() {
    return this.barracks.ownerId === -1 ? NEUTRAL_CLOTH : this.getColor();
  }

  getRingColor() {
    return this.barracks.ownerId === -1 ? NEUTRAL_RING : this.getColor();
  }

  update(barracksData, time) {
    const ownerChanged = this.barracks.ownerId !== barracksData.ownerId;
    this.barracks = barracksData;

    // Re-dye the cloth if the owner changed
    if (ownerChanged) {
      this.teamMaterial.color.setHex(this.getClothColor());
      this.teamMaterial.emissive.setHex(this.getColor());
      this.teamMaterial.emissiveIntensity = this.barracks.ownerId === -1 ? 0 : 0.15;
      if (this.rangeMesh) {
        this.rangeMesh.material.color.setHex(this.getRingColor());
      }
    }

    // Wave the flag
    if (this.flagMesh) {
      const pos = this.flagMesh.geometry.attributes.position;
      const base = this.flagMesh.userData.base;
      for (let i = 0; i < pos.count; i++) {
        const bx = base[i * 3];
        pos.setZ(i, Math.sin(time * 4 + bx * 2.5) * 0.15 * bx);
      }
      pos.needsUpdate = true;
      this.flagMesh.geometry.computeVertexNormals();
    }

    // Handle destroyed state
    const destroyed = !!this.barracks.isDestroyed;
    if (destroyed !== this.wasDestroyed) {
      this.wasDestroyed = destroyed;
      this.setDestroyedLook(destroyed);
    }

    if (destroyed) {
      // Show respawn timer through opacity
      this.structureMaterial.opacity = 0.3 + 0.2 * Math.sin(time * 4);
    } else {
      // Lantern flickers
      this.lanternMaterial.emissiveIntensity = 1.1 + Math.sin(time * 9) * 0.12 + Math.sin(time * 13.7) * 0.08;

      // Pulse range indicator for neutral barracks
      if (this.barracks.ownerId === -1 && this.rangeMesh) {
        const pulse = 0.15 + Math.sin(time * 2) * 0.1;
        this.rangeMesh.material.opacity = pulse;
      } else if (this.rangeMesh) {
        this.rangeMesh.material.opacity = 0.25;
      }
    }

    // Update health bar
    this.updateHealthBar();

    // Update healing indicator based on occupant count
    this.updateHealingIndicator(time);
  }

  // Charred, leaning and roofless while destroyed; restored on respawn.
  setDestroyedLook(destroyed) {
    this.structureMaterial.color.setHex(destroyed ? CHARRED : 0xffffff);
    this.structureMaterial.transparent = destroyed;
    this.structureMaterial.opacity = 1.0;
    this.structureMaterial.depthWrite = !destroyed;
    this.structureMaterial.needsUpdate = true;
    this.buildingMesh.rotation.z = destroyed ? 0.08 : 0;
    this.roofMesh.visible = !destroyed;
    this.flagMesh.visible = !destroyed;
    this.bannerMesh.visible = !destroyed;
    this.lanternMesh.visible = !destroyed;
    this.rangeMesh.material.opacity = destroyed ? 0.1 : 0.25;
  }

  updateHealingIndicator(time) {
    if (!this.healingIndicator) return;

    const hasOccupants = this.barracks.occupantCount > 0;
    this.healingIndicator.visible = hasOccupants && !this.barracks.isDestroyed;

    if (hasOccupants && !this.barracks.isDestroyed) {
      // Pulse the indicator
      const pulse = 0.6 + Math.sin(time * 4) * 0.4;
      this.healingIndicator.children.forEach(child => {
        if (child.material) {
          child.material.opacity = pulse;
        }
      });

      // Rotate the glow
      if (this.healingGlow) {
        this.healingGlow.rotation.z = time * 2;
      }

      // Scale pulse
      const scale = 1 + Math.sin(time * 3) * 0.1;
      this.healingIndicator.scale.set(scale, scale, scale);
    }
  }

  updateHealthBar() {
    if (!this.healthBarFill) return;

    const healthPercent = this.barracks.health / this.barracks.maxHealth;
    this.healthBarFill.scale.x = Math.max(0.01, healthPercent);
    this.healthBarFill.position.x = (healthPercent - 1) * 2; // Shift left as it decreases

    // Change color based on health
    if (healthPercent > 0.6) {
      this.healthBarFill.material.color.setHex(0x00ff00); // Green
    } else if (healthPercent > 0.3) {
      this.healthBarFill.material.color.setHex(0xffff00); // Yellow
    } else {
      this.healthBarFill.material.color.setHex(0xff0000); // Red
    }

    // Hide health bar when at full health
    this.healthBarGroup.visible = healthPercent < 1;
  }

  // Check if unit is in claiming range
  isInRange(position) {
    const dx = position.x - this.barracks.position.x;
    const dz = position.z - this.barracks.position.z;
    const distSq = dx * dx + dz * dz;
    return distSq <= this.barracks.claimRadius * this.barracks.claimRadius;
  }

  // Check if barracks can be claimed by player
  canBeClaimed(playerId) {
    if (this.barracks.isDestroyed) return false;
    if (this.barracks.ownerId === playerId) return false;
    return true;
  }

  remove() {
    this.scene.remove(this.meshGroup);
    const materials = new Set();
    this.meshGroup.traverse(child => {
      if (!child.isMesh) return;
      child.geometry.dispose();
      materials.add(child.material);
    });
    materials.forEach(material => material.dispose());
  }
}

// A hanging banner with a swallowtail hem, top edge at y = 0.
function bannerShape(width, height) {
  const w = width / 2;
  const shape = new THREE.Shape();
  shape.moveTo(-w, 0);
  shape.lineTo(w, 0);
  shape.lineTo(w, -height);
  shape.lineTo(0, -height + 0.3);
  shape.lineTo(-w, -height);
  shape.closePath();
  return shape;
}

function hashId(id) {
  let h = 2166136261;
  const str = String(id);
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
